import { useCallback, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';

type ConfirmDialogProps = {
  body: string | JSX.Element;
  title: string;
  visible: boolean;
  confirmBtnLabel?: string;
  cancelBtnLabel?: string;
  setVisible: (visible: boolean) => void;
  onConfirmFn: () => void;
  onCancelFn?: () => void;
};

/**
 *
 * ConfirmDialog
 *
 * A dialogue/modal to confirm user action such as delete
 *
 */
export default function ConfirmDialog({
  body,
  title,
  visible,
  setVisible,
  onConfirmFn = () => {},
  onCancelFn = () => {},
  confirmBtnLabel = 'Yes',
  cancelBtnLabel = 'No',
}: ConfirmDialogProps) {
  const cancelAction = useCallback(() => {
    onCancelFn();
    setVisible(false);
  }, [onCancelFn, setVisible]);

  const confirmAction = useCallback(() => {
    onConfirmFn();
    setVisible(false);
  }, [onConfirmFn, setVisible]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (visible) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          confirmAction();
        } else if (event.key === 'Escape') {
          cancelAction();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [visible, confirmAction, cancelAction]);

  return (
    <Modal show={visible} onHide={cancelAction}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{body}</Modal.Body>

      <Modal.Footer>
        <Button variant="muted" onClick={cancelAction}>
          {cancelBtnLabel}
        </Button>
        <Button variant="danger" onClick={confirmAction}>
          {confirmBtnLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
